// Master list of access-control "sub-items".
//
//   - Managed on the Sub-items page (the fixed master list).
//   - Assigned per-package on the Subscription page (CRUD), with a value.
//
// This is UI / local-state only for now — NOTHING here enforces real access in
// the app yet. The list (and packages) will later be fetched from / synced to
// backend modules; keep the shapes stable so that swap is easy.
//
//   type 'boolean' -> a package either includes it or not (toggle only)
//   type 'value'   -> a package assigns a value (e.g. patient limit = 20 / Unlimited)

// Sub-items are grouped under these two sections on the Sub-items page.
//   control = configured per subscription plan (toggle / value)
//   static  = not per-plan: baseline-for-everyone OR bespoke enterprise deployment
export const SUB_ITEM_SECTIONS = [
  { id: 'control', label: 'Control features', description: 'Toggled or valued per subscription plan.' },
  { id: 'static', label: 'Static features', description: 'Always included (baseline) or delivered per bespoke deployment — not configured per plan.' },
];

// Fixed support SLA tiers (response time), high → low. Used as the only options
// for the support_level control; no free-text. A ticket is routed to its tier.
export const SUPPORT_TIERS = ['Immediate', '12h', '24h', '48h'];

export const ACCESS_CONTROLS = [
  // ---- Control features (per-plan) ----
  { id: 'patient_limit', label: 'Patient limit', type: 'value', section: 'control', placeholder: 'e.g. 20, 50, Unlimited', description: 'Maximum number of patients a doctor can manage.' },
  { id: 'support_level', label: 'Support', type: 'value', section: 'control', options: SUPPORT_TIERS, description: 'Support response-time SLA for the plan (Immediate / 12h / 24h / 48h).' },
  { id: 'online_payment', label: 'Online payment features', type: 'boolean', section: 'control', description: 'Accept online card payments from patients.' },
  { id: 'secured_video', label: 'Secured video', type: 'boolean', section: 'control', description: 'Encrypted, HDS-compliant video consultations.' },
  { id: 'doctor_patient_chat', label: 'Doctor–patient chat', type: 'boolean', section: 'control', description: 'In-app messaging between doctor and patient.' },
  { id: 'patient_library', label: 'Patient library', type: 'boolean', section: 'control', description: 'Shared resource & document library for patients.' },
  { id: 'ai_appointment_summary', label: 'AI appointment summary', type: 'boolean', section: 'control', description: 'AI-generated summary notes for each appointment.' },
  { id: 'ai_assistant', label: 'AI Assistant', type: 'boolean', section: 'control', description: 'AI assistant / chatbot for the practice.' },
  // ---- Static features (not per-plan) ----
  { id: 'dashboard', label: 'Dashboard', type: 'boolean', section: 'static', description: 'Analytics & overview dashboard — included for every plan.' },
  { id: 'email_reminders', label: 'Email reminders', type: 'boolean', section: 'static', description: 'Appointment reminders by email — included for every plan.' },
  { id: 'sms_reminders', label: 'SMS reminders', type: 'boolean', section: 'control', description: 'Automatic appointment reminders by SMS — configured per plan.' },
  { id: 'custom_website', label: 'Custom website', type: 'boolean', section: 'static', description: 'Branded custom website — bespoke enterprise deployment.' },
  { id: 'custom_app', label: 'Custom app', type: 'boolean', section: 'static', description: 'Branded custom mobile app — bespoke enterprise deployment.' },
  { id: 'custom_integrations', label: 'Custom integrations', type: 'boolean', section: 'static', description: 'Bespoke third-party / system integrations — enterprise deployment.' },
  { id: 'custom_onboarding', label: 'Custom onboarding', type: 'boolean', section: 'static', description: 'Dedicated onboarding & setup — enterprise deployment.' },
];

// Card accent themes for packages. Full class strings live here so Tailwind's JIT
// detects them (no dynamic concatenation of color names).
export const PACKAGE_ACCENTS = {
  slate: { label: 'Slate', bar: 'from-slate-400 to-slate-500', text: 'text-slate-700', badge: 'from-slate-500 to-slate-600', soft: 'bg-slate-50', dot: 'bg-slate-400' },
  blue: { label: 'Blue', bar: 'from-blue-400 to-blue-500', text: 'text-blue-600', badge: 'from-blue-500 to-blue-600', soft: 'bg-blue-50', dot: 'bg-blue-400' },
  emerald: { label: 'Emerald', bar: 'from-emerald-400 to-teal-500', text: 'text-emerald-600', badge: 'from-emerald-500 to-teal-600', soft: 'bg-emerald-50', dot: 'bg-emerald-500' },
  amber: { label: 'Amber', bar: 'from-amber-400 to-amber-500', text: 'text-amber-600', badge: 'from-amber-500 to-amber-600', soft: 'bg-amber-50', dot: 'bg-amber-400' },
  orange: { label: 'Orange', bar: 'from-[#e07a2f] to-amber-500', text: 'text-[#b45309]', badge: 'from-[#e07a2f] to-amber-600', soft: 'bg-orange-50', dot: 'bg-[#e07a2f]' },
  teal: { label: 'Teal', bar: 'from-[#4ABDBD] to-[#2D8A8A]', text: 'text-[#3AA8A8]', badge: 'from-[#4ABDBD] to-[#2D8A8A]', soft: 'bg-[#4ABDBD]/10', dot: 'bg-[#4ABDBD]' },
  rose: { label: 'Rose', bar: 'from-rose-400 to-pink-500', text: 'text-rose-600', badge: 'from-rose-500 to-pink-600', soft: 'bg-rose-50', dot: 'bg-rose-400' },
  violet: { label: 'Violet', bar: 'from-violet-500 to-purple-600', text: 'text-violet-600', badge: 'from-violet-500 to-purple-600', soft: 'bg-violet-50', dot: 'bg-violet-500' },
  cyan: { label: 'Cyan', bar: 'from-cyan-400 to-sky-500', text: 'text-cyan-600', badge: 'from-cyan-500 to-sky-600', soft: 'bg-cyan-50', dot: 'bg-cyan-400' },
  indigo: { label: 'Indigo', bar: 'from-indigo-500 to-blue-600', text: 'text-indigo-600', badge: 'from-indigo-500 to-blue-600', soft: 'bg-indigo-50', dot: 'bg-indigo-500' },
};
export const ACCENT_KEYS = Object.keys(PACKAGE_ACCENTS);

// Card layout templates a package can use (chosen in the CRUD form).
//   bar     -> white card + accent top bar (default)
//   header  -> accent-filled header band (name + price in white)
//   dark    -> dark card with accent text
//   outline -> minimal white card with a thick accent border
export const CARD_STYLES = [
  { id: 'bar', label: 'Accent bar' },
  { id: 'header', label: 'Filled header' },
  { id: 'dark', label: 'Dark' },
  { id: 'outline', label: 'Outline' },
];
export const STYLE_KEYS = CARD_STYLES.map((s) => s.id);

// Billing periods a paid package can offer. `days` drives the subscription
// period length; admin enables the ones they want + sets a price per period.
// Mirror of the backend BILLING_PERIODS in config/accessControls.js.
export const BILLING_PERIODS = [
  { id: 'monthly', label: 'Monthly', days: 30 },
  { id: 'quarterly', label: 'Quarterly', days: 90 },
  { id: 'halfyearly', label: 'Half-year', days: 180 },
  { id: 'yearly', label: 'Yearly', days: 365 },
];

// Seed packages — mirror the 5 marketing plans, now expressed purely as
// access-control assignments. `controls[id]` is `true` for a boolean control or a
// string for a value control; absent/false means "not included in this package".
export const DEFAULT_PACKAGES = [
  {
    id: 'free', name: 'Free', price: '0', accent: 'slate', style: 'bar', popular: false, badgeText: '',
    controls: {
      patient_limit: '10', dashboard: true,
      email_reminders: true, support_level: '48h',
    },
  },
  {
    id: 'basic', name: 'Basic', price: '49', accent: 'blue', style: 'bar', popular: false, badgeText: '',
    controls: {
      patient_limit: '50', dashboard: true,
      email_reminders: true, support_level: '48h', secured_video: true, online_payment: true,
    },
  },
  {
    id: 'premium', name: 'Premium', price: '99', accent: 'emerald', style: 'header', popular: true, badgeText: 'Most Popular',
    controls: {
      patient_limit: '200', dashboard: true,
      email_reminders: true, sms_reminders: true, support_level: '24h',
      secured_video: true, doctor_patient_chat: true, patient_library: true,
      online_payment: true,
    },
  },
  {
    id: 'elite', name: 'Elite', price: '199', accent: 'violet', style: 'dark', popular: false, badgeText: '',
    controls: {
      patient_limit: 'Unlimited', dashboard: true,
      email_reminders: true, sms_reminders: true, support_level: '12h',
      secured_video: true, doctor_patient_chat: true, patient_library: true,
      online_payment: true, ai_appointment_summary: true, ai_assistant: true,
    },
  },
  {
    id: 'custom', name: 'Custom', price: '', accent: 'orange', style: 'outline', popular: false, badgeText: '',
    controls: {
      patient_limit: 'Unlimited', support_level: 'Immediate', custom_website: true, custom_app: true,
      custom_integrations: true, custom_onboarding: true, ai_assistant: true, online_payment: true,
    },
  },
];

// Whether a package includes a given control (truthy value or boolean true).
export const isControlIncluded = (controls, id) =>
  controls?.[id] !== undefined && controls?.[id] !== false && controls?.[id] !== '';

let _seq = 0;
// Local-only id generator for new packages (replaced by backend ids later).
export const nextPackageId = () => `pkg_${Date.now()}_${_seq++}`;
